import { z } from 'zod';

/**
 * Baustellenverlauf je Tag (Block L der Vorlage „PROBIG Baustelle", ADR 0070
 * Schritt 3) — zugleich der **Bau-Tagesbericht** (ADR 0074). Der Leitmonteur
 * trägt abends ein, was an diesem Tag war, und lässt gegenzeichnen.
 *
 * Bewusst EIN Datensatz für beides: derselbe Tag zweimal zu erfassen (einmal
 * für den Gesamtbericht, einmal fürs Tagesblatt) wäre die sichere Quelle für
 * abweichende Stundenangaben in zwei Dokumenten (§6).
 */

/** Wetterlagen — als Text in der DB, damit eine neue Lage keine Migration kostet. */
export const DAY_LOG_WEATHER = [
  { value: 'SONNIG', label: 'Sonnig' },
  { value: 'BEWOELKT', label: 'Bewölkt' },
  { value: 'REGEN', label: 'Regen' },
  { value: 'SCHNEE', label: 'Schnee' },
  { value: 'WIND', label: 'Wind/Sturm' },
  { value: 'HITZE', label: 'Hitze' },
  { value: 'FROST', label: 'Frost' },
] as const;

export type DayLogWeather = (typeof DAY_LOG_WEATHER)[number]['value'];

const WEATHER_VALUES: readonly string[] = DAY_LOG_WEATHER.map((option) => option.value);

/** Beschriftung einer Wetterlage; unbekannte Werte werden roh durchgereicht. */
export function weatherLabel(value: string | null): string | null {
  if (!value) return null;
  return DAY_LOG_WEATHER.find((option) => option.value === value)?.label ?? value;
}

/**
 * Uhrzeit auf der Baustelle als „HH:mm" — bewusst kein Zeitstempel. Gemeint ist
 * die Uhr vor Ort; als UTC gespeichert stünde bei einem Einsatz in Übersee eine
 * andere Zeit im Bericht als die, die der Monteur notiert hat.
 */
const timeOfDay = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit im Format HH:mm erwarten (z. B. 07:00).')
  .nullable();

/** Stunden mit Viertelstunden-Genauigkeit; 24 h ist die harte Obergrenze je Tag. */
const hours = z.number().min(0).max(24).nullable();

/**
 * Stundenspanne aus „von"/„bis", z. B. `07:00`–`16:30` → `9.5`. Dient nur als
 * Vorschlag beim Erfassen: Pausen kennt die Spanne nicht, die Stundenzahl bleibt
 * die maßgebliche Angabe.
 *
 * Endet die Zeit vor dem Anfang (Nachtschicht oder schlicht ein Zahlendreher),
 * wird bewusst NICHTS geraten — 23,5 h aus einem Tippfehler wären schlimmer als
 * ein leeres Feld.
 */
export function hoursBetween(from: string | null | undefined, to: string | null | undefined): number | null {
  const minutes = (value: string) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));
  if (!from || !to) return null;
  if (!/^\d{2}:\d{2}$/.test(from) || !/^\d{2}:\d{2}$/.test(to)) return null;
  const span = minutes(to) - minutes(from);
  if (span <= 0) return null;
  return Math.round((span / 60) * 100) / 100;
}

/** Freitextblock des Tagesberichts — großzügig bemessen, aber nicht unbegrenzt. */
const longText = z.string().trim().max(10000).nullish();

export const assemblyDayLogInputSchema = z.object({
  /** Kalendertag (ISO-Datum, ohne Uhrzeit relevant). */
  logDate: z.string().datetime(),
  /** Witterung, mehrfach möglich — „sonnig, aber windig" ist der Normalfall. */
  weatherConditions: z
    .array(z.string().refine((value) => WEATHER_VALUES.includes(value), { message: 'Unbekannte Wetterlage.' }))
    .max(DAY_LOG_WEATHER.length)
    .optional(),
  /** Frost bei −5 °C ist eine Behinderung, bei +2 °C nicht — daher als Zahl. */
  temperatureC: z.number().min(-60).max(60).nullish(),
  workFrom: timeOfDay.optional(),
  workTo: timeOfDay.optional(),
  workHours: hours.optional(),
  travelHours: hours.optional(),
  workerCount: z.number().int().min(0).max(999).nullish(),
  /** Leistungsergebnisse. */
  work: longText,
  /** Behinderungen/Erschwernisse — Grundlage für Nachträge. */
  obstructions: longText,
  /** Besondere Vorkommnisse (Begehungen, Abnahmen, Unfälle). */
  specialEvents: longText,
  /** Regieleistungen (Arbeiten nach Aufwand). */
  extraWork: longText,
});
export type AssemblyDayLogInput = z.infer<typeof assemblyDayLogInputSchema>;

/** Ändern: alle Felder optional (Auto-Save je Feld, wie beim Bericht selbst). */
export const updateAssemblyDayLogSchema = assemblyDayLogInputSchema.partial();
export type UpdateAssemblyDayLogInput = z.infer<typeof updateAssemblyDayLogSchema>;

// ── Personal (Bau-Tagesbericht) ─────────────────────────────────────────────

/**
 * Rollen/Gewerke auf der Baustelle. Die Papiervorlage zählt Hochbau-Berufe
 * (Maurer, Zimmerer, Dachdecker); im Anlagenbau sind es diese hier. Als Text in
 * der DB — eine neue Rolle kostet keine Migration.
 */
export const DAY_WORKER_ROLES = [
  { value: 'LEITMONTEUR', label: 'Leitmonteur' },
  { value: 'MONTEUR', label: 'Monteur' },
  { value: 'SCHWEISSER', label: 'Schweißer' },
  { value: 'ELEKTRIKER', label: 'Elektriker' },
  { value: 'SCHLOSSER', label: 'Schlosser' },
  { value: 'KRANFUEHRER', label: 'Kranführer' },
  { value: 'HILFSARBEITER', label: 'Hilfsarbeiter' },
  { value: 'LEHRLING', label: 'Lehrling' },
  { value: 'FREMDFIRMA', label: 'Fremdfirma' },
  { value: 'SONSTIGE', label: 'Sonstige' },
] as const;

export type DayWorkerRole = (typeof DAY_WORKER_ROLES)[number]['value'];

const WORKER_ROLE_VALUES: readonly string[] = DAY_WORKER_ROLES.map((option) => option.value);

/** Beschriftung einer Rolle; unbekannte Werte werden roh durchgereicht. */
export function workerRoleLabel(value: string): string {
  return DAY_WORKER_ROLES.find((option) => option.value === value)?.label ?? value;
}

export const assemblyDayWorkerInputSchema = z.object({
  /** Soft-Ref auf den HR-Mitarbeiter; leer bei Fremdpersonal. */
  employeeId: z.string().uuid().nullish(),
  name: z.string().trim().min(1, 'Name darf nicht leer sein.').max(200),
  role: z
    .string()
    .refine((value) => WORKER_ROLE_VALUES.includes(value), { message: 'Unbekannte Rolle.' })
    .default('MONTEUR'),
  hours: hours.optional(),
});
export type AssemblyDayWorkerInput = z.infer<typeof assemblyDayWorkerInputSchema>;

export const updateAssemblyDayWorkerSchema = assemblyDayWorkerInputSchema.partial();
export type UpdateAssemblyDayWorkerInput = z.infer<typeof updateAssemblyDayWorkerSchema>;

export const assemblyDayWorkerResponseSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid().nullable(),
  name: z.string(),
  role: z.string(),
  hours: z.number().nullable(),
  position: z.number().int(),
});
export type AssemblyDayWorkerResponse = z.infer<typeof assemblyDayWorkerResponseSchema>;

// ── Unterschriften (Bau-Tagesbericht) ───────────────────────────────────────

/** Wer unterschreibt. Beide Seiten, wie auf dem Papierformular. */
export const DAY_SIGNATURE_ROLES = [
  { value: 'BAUHERR', label: 'Bauherr / Auftraggeber' },
  { value: 'BAULEITER', label: 'Bauleiter / Monteur' },
] as const;

export type DaySignatureRole = (typeof DAY_SIGNATURE_ROLES)[number]['value'];

const SIGNATURE_ROLE_VALUES: readonly string[] = DAY_SIGNATURE_ROLES.map((option) => option.value);

export function signatureRoleLabel(value: string): string {
  return DAY_SIGNATURE_ROLES.find((option) => option.value === value)?.label ?? value;
}

/**
 * Ein Schriftzug vom Canvas, ~10–20 KB als PNG. Die Obergrenze verhindert, dass
 * jemand über dieses Feld beliebige Datenmengen in die Zeile schiebt.
 */
const SIGNATURE_MAX_CHARS = 400_000;

export const signAssemblyDaySchema = z.object({
  role: z.string().refine((value) => SIGNATURE_ROLE_VALUES.includes(value), { message: 'Unbekannte Rolle.' }),
  signerName: z.string().trim().min(2, 'Bitte den Namen der unterschreibenden Person angeben.').max(200),
  place: z.string().trim().max(200).nullish(),
  /** PNG als Data-URL, direkt aus `canvas.toDataURL()`. */
  image: z
    .string()
    .max(SIGNATURE_MAX_CHARS, 'Unterschrift ist zu groß.')
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/, 'Unterschrift muss ein PNG sein.'),
});
export type SignAssemblyDayInput = z.infer<typeof signAssemblyDaySchema>;

/**
 * In der Antwort steht bewusst NUR, wer wann wo unterschrieben hat — nicht das
 * Bild. Bei drei Wochen Einsatz wären das sonst Hunderte Kilobyte Base64 in
 * jeder Berichts-Antwort. Der Schriftzug gehört ins PDF, nicht in die Maske.
 */
export const assemblyDaySignatureResponseSchema = z.object({
  id: z.string().uuid(),
  role: z.string(),
  signerName: z.string(),
  place: z.string().nullable(),
  signedAt: z.string().datetime(),
});
export type AssemblyDaySignatureResponse = z.infer<typeof assemblyDaySignatureResponseSchema>;

export const assemblyDayLogResponseSchema = z.object({
  id: z.string().uuid(),
  /** Laufende Nummer des Tagesberichts innerhalb des Einsatzes. */
  number: z.number().int().nullable(),
  logDate: z.string().datetime(),
  weatherConditions: z.array(z.string()),
  temperatureC: z.number().nullable(),
  workFrom: z.string().nullable(),
  workTo: z.string().nullable(),
  workHours: z.number().nullable(),
  travelHours: z.number().nullable(),
  workerCount: z.number().int().nullable(),
  work: z.string().nullable(),
  obstructions: z.string().nullable(),
  specialEvents: z.string().nullable(),
  extraWork: z.string().nullable(),
  workers: z.array(assemblyDayWorkerResponseSchema),
  signatures: z.array(assemblyDaySignatureResponseSchema),
  /** Gesetzt, sobald unterschrieben wurde — der Tag ist dann unveränderlich. */
  lockedAt: z.string().datetime().nullable(),
});
export type AssemblyDayLogResponse = z.infer<typeof assemblyDayLogResponseSchema>;

/**
 * Summen des Baustellenverlaufs. **Immer gerechnet, nie getippt** (§6) — sonst
 * gäbe es zwei Wahrheiten über denselben Aufwand.
 *
 * `personHours` multipliziert die Stunden mit der Anzahl Monteure: Acht Stunden
 * mit drei Mann sind 24 geleistete Personenstunden. Ohne Angabe der Anzahl wird
 * mit einer Person gerechnet — sonst verschwände der Tag aus der Summe.
 */
export const assemblyDayLogTotalsSchema = z.object({
  dayCount: z.number().int(),
  workHours: z.number(),
  travelHours: z.number(),
  personHours: z.number(),
});
export type AssemblyDayLogTotals = z.infer<typeof assemblyDayLogTotalsSchema>;

/**
 * Personenstunden eines Tages. **Namentliche Zeilen gewinnen**: sind sie
 * erfasst, ist ihre Summe die Wahrheit — die grobe Angabe „Stunden × Anzahl"
 * ist dann nur noch die Schnellerfassung, die überschrieben wurde. Andernfalls
 * zählt sie, mit einer Person als Untergrenze (sonst verschwände der Tag).
 */
export function dayPersonHours(day: AssemblyDayLogResponse): number {
  const named = day.workers.filter((worker) => worker.hours != null);
  if (named.length > 0) return named.reduce((sum, worker) => sum + (worker.hours ?? 0), 0);
  return (day.workHours ?? 0) * Math.max(day.workerCount ?? 1, 1);
}

/** Wie viele Personen an diesem Tag vor Ort waren — namentlich, sonst gezählt. */
export function dayWorkerCount(day: AssemblyDayLogResponse): number {
  return day.workers.length > 0 ? day.workers.length : (day.workerCount ?? 0);
}

export function sumDayLogs(days: readonly AssemblyDayLogResponse[]): AssemblyDayLogTotals {
  let workHours = 0;
  let travelHours = 0;
  let personHours = 0;
  for (const day of days) {
    workHours += day.workHours ?? 0;
    travelHours += day.travelHours ?? 0;
    personHours += dayPersonHours(day);
  }
  // Auf zwei Nachkommastellen runden: Viertelstunden summieren sich in
  // Gleitkomma sonst zu „7.199999999999999".
  const round = (value: number) => Math.round(value * 100) / 100;
  return {
    dayCount: days.length,
    workHours: round(workHours),
    travelHours: round(travelHours),
    personHours: round(personHours),
  };
}

/** Eine Zeile der Gruppenaufstellung im Bau-Tagesbericht. */
export interface DayWorkerRoleTotal {
  role: string;
  label: string;
  count: number;
  hours: number;
}

/**
 * Personal je Rolle zusammenfassen — die Aufstellung, die der Bauherr vom
 * Papierformular kennt („3 Monteure, 24 Std."). Reihenfolge wie in
 * `DAY_WORKER_ROLES`, damit das PDF nicht bei jedem Tag anders sortiert ist;
 * unbekannte Rollen (aus älteren Daten) hängen hinten dran.
 */
export function sumWorkerRoles(workers: readonly AssemblyDayWorkerResponse[]): DayWorkerRoleTotal[] {
  const byRole = new Map<string, DayWorkerRoleTotal>();
  for (const worker of workers) {
    const entry = byRole.get(worker.role) ?? {
      role: worker.role,
      label: workerRoleLabel(worker.role),
      count: 0,
      hours: 0,
    };
    entry.count += 1;
    entry.hours += worker.hours ?? 0;
    byRole.set(worker.role, entry);
  }
  const order = (role: string) => {
    const index = WORKER_ROLE_VALUES.indexOf(role);
    return index === -1 ? WORKER_ROLE_VALUES.length : index;
  };
  return [...byRole.values()]
    .map((entry) => ({ ...entry, hours: Math.round(entry.hours * 100) / 100 }))
    .sort((a, b) => order(a.role) - order(b.role) || a.label.localeCompare(b.label, 'de'));
}
